import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MapPin, Calendar, Users, CreditCard, Building } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function PackageBookingPage() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const packageData = location.state?.package;
  const selectedVariant = location.state?.selectedVariant;
  const variantIndex = location.state?.variantIndex;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    num_travelers: 1,
    travel_date: '',
    special_requests: '',
    payment_method: 'online' // 'online' or 'at_hotel'
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.travel_date) {
      toast.error('Please fill in all required fields');
      return false;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    // Validate phone
    const phoneRegex = /^[\d\s\+\-\(\)]{10,}$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error('Please enter a valid phone number');
      return false;
    }

    // Validate number of travelers
    const maxPeople = selectedVariant?.max_people || packageData?.max_people || 10;
    if (formData.num_travelers < 1 || formData.num_travelers > maxPeople) {
      toast.error(`Number of travelers must be between 1 and ${maxPeople}`);
      return false;
    }

    return true;
  };

  const calculateTotal = () => {
    if (!selectedVariant && !packageData) return 0;
    const price = selectedVariant?.price || packageData?.price || 0;
    return price * formData.num_travelers;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      // Create booking
      const bookingData = {
        package_id: packageData.id || packageData._id,
        package_name: packageData.name,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        num_travelers: parseInt(formData.num_travelers),
        travel_date: formData.travel_date,
        special_requests: formData.special_requests,
        payment_method: formData.payment_method,
        total_amount: calculateTotal(),
        booking_status: 'pending',
        // Include variant information
        selected_variant: selectedVariant ? {
          duration_text: selectedVariant.duration_text,
          duration_days: selectedVariant.duration_days,
          price: selectedVariant.price,
          variant_index: variantIndex
        } : null
      };

      const response = await axios.post(`${API_URL}/api/package-bookings`, bookingData);

      if (formData.payment_method === 'online') {
        // Redirect to payment gateway
        toast.success('Redirecting to payment gateway...');
        // TODO: Integrate with actual payment gateway (Cashfree)
        navigate('/payment-success', { 
          state: { 
            bookingId: response.data.id || response.data._id,
            type: 'package'
          } 
        });
      } else {
        // Pay at hotel - booking confirmed
        toast.success('Booking confirmed! You can pay at the hotel.');
        navigate('/booking-success', { 
          state: { 
            booking: response.data,
            type: 'package'
          } 
        });
      }

    } catch (error) {
      console.error('Booking failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!packageData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Package not found</h2>
          <Button onClick={() => navigate('/nammayatra')}>
            Back to Packages
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Complete Your Booking</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Traveler Details</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      required
                    />
                  </div>

                  {/* Number of Travelers */}
                  <div>
                    <Label htmlFor="num_travelers">Number of Travelers *</Label>
                    <Input
                      id="num_travelers"
                      name="num_travelers"
                      type="number"
                      min="1"
                      max={packageData.max_people}
                      value={formData.num_travelers}
                      onChange={handleChange}
                      required
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Maximum {packageData.max_people} travelers allowed
                    </p>
                  </div>

                  {/* Travel Date */}
                  <div>
                    <Label htmlFor="travel_date">Preferred Travel Date *</Label>
                    <Input
                      id="travel_date"
                      name="travel_date"
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      value={formData.travel_date}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Special Requests */}
                  <div>
                    <Label htmlFor="special_requests">Special Requests (Optional)</Label>
                    <Textarea
                      id="special_requests"
                      name="special_requests"
                      value={formData.special_requests}
                      onChange={handleChange}
                      placeholder="Any special requirements or requests..."
                      rows={4}
                    />
                  </div>

                  {/* Payment Method */}
                  <div>
                    <Label className="mb-3 block">Payment Method *</Label>
                    <div className="space-y-3">
                      <div
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          formData.payment_method === 'online'
                            ? 'border-green-600 bg-green-50'
                            : 'border-gray-200 hover:border-green-300'
                        }`}
                        onClick={() => setFormData(prev => ({ ...prev, payment_method: 'online' }))}
                      >
                        <div className="flex items-center space-x-3">
                          <CreditCard className="w-5 h-5 text-green-600" />
                          <div className="flex-1">
                            <p className="font-semibold">Pay Online</p>
                            <p className="text-sm text-gray-600">Secure payment via credit/debit card, UPI, net banking</p>
                          </div>
                          <input
                            type="radio"
                            name="payment_method"
                            value="online"
                            checked={formData.payment_method === 'online'}
                            onChange={handleChange}
                            className="w-4 h-4"
                          />
                        </div>
                      </div>

                      <div
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                          formData.payment_method === 'at_hotel'
                            ? 'border-green-600 bg-green-50'
                            : 'border-gray-200 hover:border-green-300'
                        }`}
                        onClick={() => setFormData(prev => ({ ...prev, payment_method: 'at_hotel' }))}
                      >
                        <div className="flex items-center space-x-3">
                          <Building className="w-5 h-5 text-green-600" />
                          <div className="flex-1">
                            <p className="font-semibold">Pay at Hotel</p>
                            <p className="text-sm text-gray-600">Pay when you arrive at the destination</p>
                          </div>
                          <input
                            type="radio"
                            name="payment_method"
                            value="at_hotel"
                            checked={formData.payment_method === 'at_hotel'}
                            onChange={handleChange}
                            className="w-4 h-4"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-3"
                  >
                    {loading ? 'Processing...' : formData.payment_method === 'online' ? 'Proceed to Payment' : 'Confirm Booking'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Booking Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-bold text-lg mb-2">{packageData.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      {packageData.destination}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Calendar className="w-4 h-4 mr-2" />
                      {selectedVariant?.duration_text || packageData.duration || 'Duration not specified'}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Users className="w-4 h-4 mr-2" />
                      {formData.num_travelers} traveler(s)
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Price per person</span>
                    <span className="font-semibold">₹{(selectedVariant?.price || packageData?.price || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Number of travelers</span>
                    <span className="font-semibold">× {formData.num_travelers}</span>
                  </div>
                  <div className="flex justify-between font-bold text-xl pt-3 border-t-2 border-green-600">
                    <span className="text-gray-900">Total Amount</span>
                    <span className="text-green-700">₹{(calculateTotal() || 0).toLocaleString()}</span>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                  <p className="text-yellow-800">
                    💡 <strong>Note:</strong> Final confirmation will be sent to your email within 24 hours.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
